import type {
  RealtimeFilters,
  RpcName,
  StringRecord,
  TableColumnsMap,
  TableNameRecord,
  Tables,
} from "@/types";

// i18n field-bag keys (reserved for future translatable-content features)
export const I18N_TRANSLATIONS_KEY = "translations";
export const I18N_VERSIONED_BASE_KEY = "base";
export const I18N_VERSIONED_CONTRACT_KEY = "contract";

export const SCHEMAS = {
  PUBLIC: "public",
} as const;

// LoyaBoost v1 tables (see supabase/schemas/*)
export const TABLES = {
  // Core auth & roles
  ROLES: "roles",
  USER_ROLES: "user_roles",
  USERS: "users",
  USER_PREFERENCES: "user_preferences",
  USER_PROFILES: "user_profiles",
  // i18n / infra
  LANGUAGES: "languages",
  // Multi-tenant
  MERCHANTS: "merchants",
  MERCHANT_USERS: "merchant_users",
  MERCHANT_LOCATIONS: "merchant_locations",
  MEMBERS: "members",
  MEMBER_PROFILES: "member_profiles",
  POINT_TRANSACTIONS: "point_transactions",
  MEMBER_MESSAGES: "member_messages",
  MEMBER_MESSAGE_DELIVERIES: "member_message_deliveries",
  APPLE_PASS_REGISTRATIONS: "apple_pass_registrations",
  GOOGLE_PASS_REGISTRATIONS: "google_pass_registrations",
  VAULT_KEYS: "vault_keys",
  FILES: "files",
  MERCHANT_FILE_QUOTA_OVERRIDES: "merchant_file_quota_overrides",
  FILE_ORPHAN_AUDITS: "file_orphan_audits",
} as const satisfies TableNameRecord;

export const COLUMNS_BY_TABLE = {
  [TABLES.ROLES]: ["id", "name", "description", "created_at"],
  [TABLES.USER_ROLES]: ["user_id", "role_id", "created_at"],
  [TABLES.USERS]: [
    "id",
    "email",
    "email_confirmed_at",
    "confirmed_at",
    "last_sign_in_at",
    "first_name",
    "last_name",
    "phone",
    "phone_confirmed_at",
    "is_sso_user",
    "is_anonymous",
    "is_super_admin",
    "banned_until",
    "deleted_at",
    "auth_app_metadata",
    "auth_user_metadata",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.USER_PREFERENCES]: [
    "user_id",
    "prefs",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.USER_PROFILES]: [
    "user_id",
    "language_id",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.LANGUAGES]: [
    "id",
    "code",
    "name_native",
    "name_en",
    "is_default",
    "is_enabled",
  ],
  [TABLES.MERCHANTS]: [
    "id",
    "code",
    "name",
    "billing_status",
    "plan_tier",
    "default_locale",
    "brand_color",
    "logo_url",
    "owner_name",
    "contact_email",
    "website_url",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.MERCHANT_LOCATIONS]: [
    "id",
    "merchant_id",
    "label",
    "address_line",
    "address_city",
    "address_postal_code",
    "address_country",
    "phone",
    "is_primary",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.MEMBER_PROFILES]: [
    "id",
    "email",
    "phone",
    "first_name",
    "last_name",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.POINT_TRANSACTIONS]: [
    "id",
    "member_id",
    "delta",
    "reason",
    "note",
    "balance_after",
    "created_at",
    "created_by",
  ],
  [TABLES.MERCHANT_USERS]: [
    "merchant_id",
    "user_id",
    "role_in_merchant",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.MEMBERS]: [
    "id",
    "merchant_id",
    "profile_id",
    "email",
    "phone",
    "first_name",
    "last_name",
    "balance_points",
    "access_token",
    "auth_user_id",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.APPLE_PASS_REGISTRATIONS]: [
    "id",
    "member_id",
    "device_library_identifier",
    "pass_type_identifier",
    "serial_number",
    "last_pushed_at",
    "created_at",
    "updated_at",
  ],
  [TABLES.GOOGLE_PASS_REGISTRATIONS]: [
    "id",
    "member_id",
    "object_id",
    "class_id",
    "saved_at",
    "removed_at",
    "last_pushed_at",
    "created_at",
    "updated_at",
  ],
  [TABLES.VAULT_KEYS]: [
    "id",
    "code",
    "name",
    "value",
    "is_required",
    "value_set_at",
    "created_at",
    "updated_at",
  ],
  [TABLES.FILES]: [
    "id",
    "merchant_id",
    "owner_user_id",
    "filename",
    "original_filename",
    "content_hash",
    "file_type",
    "mime_type",
    "size_bytes",
    "visibility",
    "storage_bucket",
    "storage_path",
    "width",
    "height",
    "source_kind",
    "source_ref",
    "origin",
    "is_processed",
    "processing_error",
    "deleted_at",
    "metadata",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.MERCHANT_FILE_QUOTA_OVERRIDES]: [
    "merchant_id",
    "total_quota_bytes",
    "max_file_size_bytes",
    "reason",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
  ],
  [TABLES.FILE_ORPHAN_AUDITS]: [
    "id",
    "storage_bucket",
    "object_name",
    "size_bytes",
    "detected_at",
    "resolved_at",
    "resolution",
    "created_at",
    "updated_at",
  ],
} as const satisfies TableColumnsMap;

export const PRIMARY_KEYS_BY_TABLE = {
  [TABLES.ROLES]: ["id"],
  [TABLES.USER_ROLES]: ["user_id", "role_id"],
  [TABLES.USERS]: ["id"],
  [TABLES.USER_PREFERENCES]: ["user_id"],
  [TABLES.USER_PROFILES]: ["user_id"],
  [TABLES.LANGUAGES]: ["id"],
  [TABLES.MERCHANTS]: ["id"],
  [TABLES.MERCHANT_USERS]: ["merchant_id", "user_id"],
  [TABLES.MERCHANT_LOCATIONS]: ["id"],
  [TABLES.MEMBERS]: ["id"],
  [TABLES.MEMBER_PROFILES]: ["id"],
  [TABLES.POINT_TRANSACTIONS]: ["id"],
  [TABLES.APPLE_PASS_REGISTRATIONS]: ["id"],
  [TABLES.GOOGLE_PASS_REGISTRATIONS]: ["id"],
  [TABLES.VAULT_KEYS]: ["id"],
  [TABLES.FILES]: ["id"],
  [TABLES.MERCHANT_FILE_QUOTA_OVERRIDES]: ["merchant_id"],
  [TABLES.FILE_ORPHAN_AUDITS]: ["id"],
} as const satisfies TableColumnsMap;

// Tables whose primary display column differs from "name" (default)
export const DISPLAY_COLUMNS: Readonly<StringRecord> = {
  members: "email",
};

export const SCHEMAS_BY_TABLE = Object.fromEntries(
  Object.values(TABLES).map((t) => [t, SCHEMAS.PUBLIC]),
);

// Realtime: tables we subscribe to so React-Query caches stay in sync with
// async DB-driven state changes (e.g. files.is_processed flipping after the
// finalize-upload trigger runs).
export const REALTIME_TABLES = [
  TABLES.FILES,
] as const satisfies readonly Tables[];

export const REALTIME_FILTERS: RealtimeFilters<
  (typeof REALTIME_TABLES)[number]
> = {};

export const RPCS = {
  USER_PREFERENCES_PATCH: "user_preferences_patch",
  SEND_MEMBER_MESSAGE: "send_member_message",
  SEND_MERCHANT_CAMPAIGN: "send_merchant_campaign",
  CREATE_MEMBER: "create_member",
  UPDATE_MEMBER: "update_member",
  DELETE_MEMBER: "delete_member",
  UPDATE_IDENTITY: "update_identity",
  DELETE_IDENTITY: "delete_identity",
  CREATE_CARD_FOR_IDENTITY: "create_card_for_identity",
  BOOK_MEMBER_POINTS: "book_member_points",
  TOGGLE_USER_PLATFORM_ROLE: "toggle_user_platform_role",
  GET_VAULT_KEY: "get_vault_key",
  FILES_SOFT_DELETE: "files_soft_delete",
  FILES_RESTORE: "files_restore",
  FILES_HARD_DELETE: "files_hard_delete",
  FILES_SOFT_DELETE_MANY: "files_soft_delete_many",
  FILES_RESTORE_MANY: "files_restore_many",
  FILES_HARD_DELETE_MANY: "files_hard_delete_many",
  FILES_MERCHANT_USAGE: "files_merchant_usage",
} as const satisfies Record<string, RpcName>;
